import {
  Alert,
  AlertTitle,
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { Karta } from '@/components/Karta';
import { PasSekcji } from '@/components/PasSekcji';
import { DateRangeFilter } from '@/components/DateRangeFilter';
import { NaglowekEkranu } from '@/components/NaglowekEkranu';
import { DataQualityBadge, ErrorState, LoadingState } from '@/components/states';
import { MiniMetryka, PlytaGlowna } from '@/components/PlytaGlowna';
import type { Dowod } from '@/components/SzufladaDowodu';
import { BrakDostepuDoDostaw } from './BrakDostepuDoDostaw';
import { czyWidziDostawy } from '@/lib/auth/dostepZrodel';
import {
  KLASYFIKACJA_DRIVE,
  OKNO_TOLERANCJI,
  PaskKlasyfikacjiZrodla,
  SWIEZOSC_DRIVE,
  ZRODLO_HISTORY,
  ZnacznikWyniku,
  formatPct,
  grupaStatusu,
  ocenKompletnosc,
  udzial,
  type Kompletnosc,
} from './punktualnosc';
import { useAuth } from '@/lib/auth/AuthContext';
import { formatCount, formatRange } from '@/lib/format';
import { czyPustyZbior, wartoscDoPokazania } from '@/lib/pustyZbior';
import { useDeliveryHistory } from '@/lib/useAnalytics';
import { useDateRange } from '@/lib/useDateRange';

/**
 * HISTORIA DOSTAW — migracja do języka „SYGNAŁ".
 *
 * Pytanie ekranu: KTÓRY KONKRETNY REKORD STOI ZA LICZBAMI z pozostałych ekranów dostaw.
 *
 * To jest DOWÓD leżący pod metrykami, więc obowiązuje go ostrzejsza reguła niż ekran z
 * agregatami: dowód, który po cichu pomija część zdarzeń, dowodem nie jest. Dlatego ucięcie
 * listy jest napisane wprost, a rozkład wyników policzony z widocznych rekordów jest pokazywany
 * TYLKO wtedy, gdy wiadomo, że widać komplet. Statystyka z ucięcia wygląda dokładnie tak samo
 * jak statystyka z całości — i to jest jej najgroźniejsza cecha.
 */

const OPIS_KOMPLETNOSCI: Record<Kompletnosc, string> = {
  KOMPLETNA: 'lista obejmuje wszystkie rekordy pasujące do zapytania',
  UCIETA: 'lista jest ucięta limitem — widać wycinek, nie całość',
  NIEUSTALONA: 'backend nie podaje liczby rekordów pasujących do zapytania, więc kompletności nie da się stwierdzić',
};

export function DriveHistoryPage() {
  const auth = useAuth();
  const [range] = useDateRange();
  const [params] = useSearchParams();
  const schoolId = params.get('school_id');
  const { data, isLoading, error } = useDeliveryHistory(
    schoolId && /^\d+$/.test(schoolId) ? { school_id: Number(schoolId) } : {},
  );

  if (!czyWidziDostawy(auth)) return <BrakDostepuDoDostaw />;

  const windowLabel = formatRange(range.date_from, range.date_to);
  const wiersze = data?.history ?? [];

  const kompletnosc = data
    ? ocenKompletnosc({ count: data.count, total: data.total, limit: data.limit, truncated: data.truncated })
    : 'NIEUSTALONA';

  /*
   * Rozkład wyników liczony z WIDOCZNYCH rekordów. Przy uciętej albo nieustalonej liście nie
   * pokazujemy go wcale — nie dlatego, że nie da się go policzyć, tylko dlatego, że policzony
   * wyglądałby jak rozkład całego okna. Ekran, który raz pokaże wycinek jako całość, traci prawo
   * do bycia dowodem.
   */
  const rozklad = wiersze.reduce(
    (acc, row) => {
      const grupa = grupaStatusu(row.status);
      if (grupa === 'na-czas') acc.naCzas += 1;
      else if (grupa === 'przed-czasem') acc.przedCzasem += 1;
      else if (grupa === 'spoznione') acc.spoznione += 1;
      else acc.nieznane += 1;
      return acc;
    },
    { naCzas: 0, przedCzasem: 0, spoznione: 0, nieznane: 0 },
  );

  /*
   * ZERO TO NIE TO SAMO CO PUSTO. Ziarno listy: wiersze to rekordy dostaw, byty to różne szkoły,
   * które w tych rekordach występują. Układ „rekordy są, ale żadnego nie da się przypisać do
   * szkoły" NIE jest pustką — jest defektem złączenia i ma prawo wyglądać inaczej.
   */
  const ziarno = {
    wierszy: wiersze.length,
    bytow: new Set(wiersze.map((r) => r.school_name).filter((n): n is string => Boolean(n))).size,
  };
  const pusteZrodlo = czyPustyZbior(ziarno);
  const liczbaRekordow = wartoscDoPokazania(data?.count ?? null, ziarno);
  const rozkladWiarygodny = kompletnosc === 'KOMPLETNA' && wiersze.length > 0;
  const kafelek = (v: number): string | null => (rozkladWiarygodny ? formatCount(v) : null);

  const dowodHistorii: Dowod = {
    tytul: 'Rekordy dostaw w widoku',
    etykietaWartosci: 'Rekordy zwrócone przez źródło',
    wartosc: liczbaRekordow === null ? null : formatCount(liczbaRekordow),
    podstawa: { time: 'DELIVERY_DATE' },
    zrodlo: ZRODLO_HISTORY,
    klasyfikacja: KLASYFIKACJA_DRIVE,
    swiezosc: SWIEZOSC_DRIVE,
    oknoBiezace: windowLabel,
    wyliczenie:
      'Rekordy dostaw pasujące do okna i do filtrów, zwrócone przez źródło i pokazane bez agregacji. ' +
      'Każdy wiersz to jedno zdarzenie zapisane przez aplikację kierowcy.',
    szczegoly: [
      ['Rekordów w widoku', formatCount(data?.count ?? null)],
      ['Rekordów pasujących do zapytania', formatCount(data?.total ?? null)],
      ['Limit zapytania', formatCount(data?.limit ?? null)],
      ['Kompletność', OPIS_KOMPLETNOSCI[kompletnosc]],
      ['Filtr szkoły', schoolId && /^\d+$/.test(schoolId) ? `school_id = ${schoolId}` : 'brak — wszystkie szkoły'],
      ['Na czas w widoku', rozkladWiarygodny ? formatCount(rozklad.naCzas) : 'nie liczone przy niepełnej liście'],
      [
        'Przed czasem w widoku',
        rozkladWiarygodny ? formatCount(rozklad.przedCzasem) : 'nie liczone przy niepełnej liście',
      ],
      ['Spóźnione w widoku', rozkladWiarygodny ? formatCount(rozklad.spoznione) : 'nie liczone przy niepełnej liście'],
      ['Status spoza znanego zestawu', formatCount(rozklad.nieznane)],
    ],
    proba: `${formatCount(wiersze.length)} rekordów w widoku`,
    ograniczenia: [
      OKNO_TOLERANCJI,
      'Kolumna „planowana" to godzina zapisana na rekordzie w chwili dostawy (break_time_snapshot), a nie ' +
        'bieżąca godzina przerwy szkoły — dzięki temu późniejsza zmiana godziny w panelu nie przepisuje historii.',
      'Status pojedynczego rekordu jest utrwalany przez system kierowców w chwili dostawy. Control Center go ' +
        'wyłącznie odczytuje i nie przelicza go względem bieżących ustawień szkoły.',
      ...(kompletnosc !== 'KOMPLETNA'
        ? [
            `Lista nie jest potwierdzona jako pełna (${OPIS_KOMPLETNOSCI[kompletnosc]}). Rozkład wyników z ` +
              'widocznych rekordów NIE jest rozkładem okna i dlatego nie jest tu pokazany.',
          ]
        : []),
      ...(pusteZrodlo
        ? ['ŹRÓDŁO ZWRÓCIŁO PUSTY ZBIÓR: zero rekordów w tym oknie. To nie jest zmierzone zero dostaw.']
        : []),
    ],
    czegoNieWynika:
      'Z liczby rekordów nie wynika, ile dostaw faktycznie pojechało: lista pokazuje zdarzenia ZAPISANE w ' +
      'aplikacji kierowcy, a dostawa niepotwierdzona nie zostawia tu żadnego śladu — ani jako spóźniona, ani ' +
      'jako brak. Nie wynika z niej także żaden związek przyczynowy: obok siebie stoją kierowca, restauracja ' +
      'i odchylenie, ale ich współwystępowanie w jednym wierszu niczego nie dowodzi.',
  };

  return (
    <Box>
      <NaglowekEkranu
        tytul="Historia dostaw"
        opis="Który konkretny rekord stoi za liczbami z pozostałych ekranów dostaw — i o ile godzina faktyczna rozminęła się z planowaną."
        akcja={<DateRangeFilter wariant="naglowek" />}
      />

      <PaskKlasyfikacjiZrodla />

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: '86ch' }}>
        Kolumna „planowana” to godzina zapisana na rekordzie w chwili dostawy
        (<code>break_time_snapshot</code>), nie bieżąca godzina przerwy szkoły. Dzięki temu późniejsza
        zmiana godziny w panelu nie przepisuje historii.
        {schoolId ? ' Widok zawężony do jednej szkoły.' : ''}
      </Typography>

      {isLoading ? <LoadingState /> : null}
      {error ? <ErrorState error={error} /> : null}

      {!isLoading && !error ? (
        <Stack spacing={3} className="sygnal-kaskada">
          <PlytaGlowna
            etykieta="Rekordy dostaw w widoku"
            wartosc={liczbaRekordow === null ? null : formatCount(liczbaRekordow)}
            opis={
              <Typography variant="body2">
                {pusteZrodlo
                  ? 'źródło nie zwróciło ani jednego rekordu w tym oknie — to nie jest zmierzone zero dostaw, tylko brak zapisanych zdarzeń'
                  : 'surowe zdarzenia zapisane przez aplikację kierowcy — to jest dowód leżący pod liczbami z pozostałych ekranów dostaw'}
              </Typography>
            }
            zrodlo={ZRODLO_HISTORY}
            okno={`${windowLabel} · oś czasu: dzień dostawy`}
            dowod={dowodHistorii}
            stopka={
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.72)' }}>
                {OPIS_KOMPLETNOSCI[kompletnosc]}
              </Typography>
            }
            boczne={
              <>
                <MiniMetryka wartosc={kafelek(rozklad.naCzas)} etykieta="na czas w widoku" />
                <MiniMetryka wartosc={kafelek(rozklad.przedCzasem)} etykieta="przed czasem w widoku" />
                <MiniMetryka wartosc={kafelek(rozklad.spoznione)} etykieta="spóźnione w widoku" />
                <MiniMetryka
                  wartosc={rozkladWiarygodny ? formatPct(udzial(rozklad.naCzas, wiersze.length)) : null}
                  etykieta="udział na czas w widoku"
                />
              </>
            }
          />

          {!rozkladWiarygodny && wiersze.length > 0 ? (
            <Alert severity="warning">
              <AlertTitle>Rozkład wyników nie jest liczony z tej listy</AlertTitle>
              <Typography variant="body2">
                {OPIS_KOMPLETNOSCI[kompletnosc]}, więc podział na „na czas / przed czasem / spóźnione”
                policzony z widocznych rekordów byłby rozkładem wycinka, a wyglądałby jak rozkład całego
                okna. Podział dla całego okna jest na ekranie „Punktualność dostaw”.
              </Typography>
            </Alert>
          ) : null}

          {wiersze.length === 0 ? (
            <Alert severity="warning" icon={false}>
              <AlertTitle>Brak dostaw w wybranym oknie</AlertTitle>
              <Typography variant="body2">
                Źródło nie zwróciło ani jednego rekordu dla okna {windowLabel}
                {schoolId ? ' przy zawężeniu do jednej szkoły' : ''}. To jest stan źródła, a nie wynik
                operacyjny: nie wiadomo, czy dowozów nie było, czy nie zostały zapisane.
              </Typography>
            </Alert>
          ) : (
            <>
              {/*
                Historia jest DOWODEM leżącym pod metrykami. Dowód, który po cichu pomija część
                zdarzeń, dowodem nie jest — dlatego ucięcie listy musi być napisane wprost,
                a nie pozostawione do wywnioskowania z porównania liczb.
              */}
              <Typography variant="body2" color="text.secondary">
                {kompletnosc === 'UCIETA' && data && typeof data.total === 'number'
                  ? `${formatCount(data.count)} z ${formatCount(data.total)} rekordów — lista ucięta do ${formatCount(
                      data.limit ?? data.count,
                    )}. Zawęź okno albo filtr, żeby zobaczyć komplet.`
                  : null}
                {kompletnosc === 'UCIETA' && (!data || typeof data.total !== 'number')
                  ? `${formatCount(data?.count ?? wiersze.length)} rekordów — lista ucięta limitem zapytania. Źródło nie podaje, ile rekordów pasuje do zapytania, więc nie wiadomo, ile zostało poza widokiem. Zawęź okno albo filtr.`
                  : null}
                {kompletnosc === 'NIEUSTALONA'
                  ? `${formatCount(data?.count ?? wiersze.length)} rekordów — kompletności tej listy NIE da się stwierdzić: źródło nie podaje ani liczby rekordów pasujących do zapytania, ani informacji o ucięciu.`
                  : null}
                {kompletnosc === 'KOMPLETNA' ? `${formatCount(data?.count ?? wiersze.length)} rekordów.` : null}
              </Typography>

              <PasSekcji
                tytul="Rejestr dostaw"
                opis="Pojedyncze rekordy — to z nich powstaje każda liczba na pozostałych ekranach."
              />

              <Karta nadtytul="Jedna dostawa = jeden wiersz" tytul="Historia dostaw">
              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table size="small" aria-label="Historia dostaw">
                  <TableHead>
                    <TableRow>
                      <TableCell>Data</TableCell>
                      <TableCell>Szkoła</TableCell>
                      <TableCell>Restauracja</TableCell>
                      <TableCell>Kierowca (ID)</TableCell>
                      <TableCell align="right">Planowana</TableCell>
                      <TableCell align="right">Faktyczna</TableCell>
                      <TableCell align="right">Odchylenie</TableCell>
                      <TableCell>Wynik</TableCell>
                      <TableCell>Źródło</TableCell>
                      <TableCell>Jakość</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {wiersze.map((row) => (
                      <TableRow key={row.id} hover>
                        <TableCell className="sygnal-liczba">{row.delivery_date}</TableCell>
                        <TableCell>{row.school_name ?? '—'}</TableCell>
                        <TableCell>{row.restaurant_name ?? '—'}</TableCell>
                        {/* Identyfikator, nie nazwisko — patrz kontrakt DeliveryHistoryRow. */}
                        <TableCell className="sygnal-liczba">{row.driver_ref ?? '—'}</TableCell>
                        <TableCell align="right" className="sygnal-liczba">
                          {row.planned_time ?? '—'}
                        </TableCell>
                        <TableCell align="right" className="sygnal-liczba">
                          {row.actual_time ?? '—'}
                        </TableCell>
                        <TableCell align="right" className="sygnal-liczba">
                          {row.delta_min === null ? '—' : `${row.delta_min > 0 ? '+' : ''}${row.delta_min} min`}
                        </TableCell>
                        <TableCell>
                          <ZnacznikWyniku status={row.status} />
                        </TableCell>
                        <TableCell>{row.source ?? '—'}</TableCell>
                        <TableCell>
                          <DataQualityBadge quality={row.data_quality} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              </Karta>

              <Typography variant="caption" sx={{ color: 'var(--tekst-slaby)', display: 'block', maxWidth: '86ch' }}>
                Kolumna „wynik” pokazuje status utrwalony w chwili dostawy: na czas, przed czasem albo jeden z
                kubełków spóźnienia (do 5 min, do 15 min, ponad 15 min). Status spoza tego zestawu jest
                pokazywany dosłownie i nie jest doklejany do żadnej z trzech kategorii. Pusta godzina faktyczna
                albo puste odchylenie to brak zapisu, nie zero.
              </Typography>
            </>
          )}
        </Stack>
      ) : null}
    </Box>
  );
}
